import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Voicemail, PhoneOff, PhoneMissed, ThumbsUp,
  ThumbsDown, CalendarClock, MessageCircle, Ban } from "lucide-react";

// Outcome key → { label, icon, tone, hintForNote }
const OUTCOMES = [
  { k: "voicemail",      label: "Voicemail",       icon: Voicemail,      tone: "amber" },
  { k: "no_answer",      label: "No answer",       icon: PhoneMissed,    tone: "neutral" },
  { k: "answered",       label: "Answered",        icon: MessageCircle,  tone: "neutral" },
  { k: "interested",     label: "Interested",      icon: ThumbsUp,       tone: "green" },
  { k: "callback",       label: "Callback later",  icon: CalendarClock,  tone: "blue" },
  { k: "not_interested", label: "Not interested",  icon: ThumbsDown,     tone: "red" },
  { k: "wrong_number",   label: "Wrong number",    icon: PhoneOff,       tone: "red" },
  { k: "dnc",            label: "Mark as DNC",     icon: Ban,            tone: "red" },
];

const TONE_CLS = {
  amber:   "border-amber-300 hover:bg-amber-50",
  green:   "border-emerald-300 hover:bg-emerald-50",
  blue:    "border-blue-300 hover:bg-blue-50",
  red:     "border-red-300 hover:bg-red-50",
  neutral: "border-[var(--app-line)] hover:bg-[var(--app-surface)]",
};

/**
 * CallOutcomeDialog — pops after a manual call so the rep can log outcome
 * (voicemail / interested / callback / DNC …) plus an optional note in one tap.
 *
 * Props:
 *   open, onOpenChange   — controlled state
 *   leadId               — required
 *   onLogged(outcome)    — called after successful save (parent refreshes lead)
 */
export default function CallOutcomeDialog({
  open, onOpenChange, leadId, onLogged,
}) {
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (outcome) => {
    if (!leadId) return;
    setBusy(true);
    try {
      await api.post(`/leads/${leadId}/call-outcome`, {
        outcome, note: note.trim() || null,
      });
      toast.success(`Logged: ${outcome.replace("_", " ")}`);
      onLogged?.(outcome);
      onOpenChange(false);
      setSelected(null);
      setNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-md" data-testid="call-outcome-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">How did the call go?</DialogTitle>
          <DialogDescription>
            One-tap logging — we&apos;ll add it to the lead&apos;s timeline.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2">
          {OUTCOMES.map((o) => {
            const Icon = o.icon;
            const isSel = selected === o.k;
            return (
              <button
                key={o.k}
                onClick={() => setSelected(o.k)}
                disabled={busy}
                className={`px-3 py-2.5 rounded-lg border text-sm text-left flex items-center gap-2 transition-colors ${
                  isSel ? "border-[var(--app-black)] bg-[var(--app-surface)]" : TONE_CLS[o.tone]
                }`}
                data-testid={`outcome-${o.k}`}
              >
                <Icon size={14} />
                <span>{o.label}</span>
              </button>
            );
          })}
        </div>

        <div>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note (e.g. 'Wants to sell in Q2, callback next week')"
            rows={3}
            className="field-sharp"
            data-testid="outcome-note"
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Skip
          </Button>
          <Button
            onClick={() => submit(selected)}
            disabled={!selected || busy}
            data-testid="outcome-save"
          >
            {busy ? <Loader2 className="animate-spin mr-2" size={13} /> : null}
            Save outcome
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
